// src/pages/profile-settings/patient/Preferences.jsx
import React from "react";
import { useNavigate } from "react-router-dom";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import List from "@/sot/primitives/List.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

export default function Preferences() {
  const nav = useNavigate();
  const { languageCode, t } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const copy = React.useCallback(
    (key, fr, en) => (isFrench ? fr : t(key, en)),
    [isFrench, t]
  );

  return (
    <SettingsScreenShell>
      <TopBarCenter
        close
        onBack={() => nav("/settings/patient")}
        title={copy("settings.preferences.title", "Préférences", "Preferences")}
      />

      <div className="space-y-8 mt-2">
        {/* App section */}
        <section className="space-y-3">
          <h2 className="text-title-small text-on-surface-var">
            {copy("settings.preferences.sections.app", "Application", "App")}
          </h2>

          <List
            items={[
              {
                leading: "/icons/list-items/description_fill.svg",
                title: copy("settings.preferences.healthRecordsSharing.title", "Dossiers de santé et partage", "Health records & sharing"),
                subtitle: copy("settings.preferences.healthRecordsSharing.subtitle", "Mon dossier de santé et partage des orientations", "My Electronic Health Record and referral sharing"),
                trailing: <span className="text-body-small text-[rgb(var(--afrimed-primary))]">{copy("common.manage", "Gérer", "Manage")}</span>,
                onClick: () => nav("/settings/patient/preferences/health-records-sharing"),
              },
              {
                leading: "/icons/list-items/sun-moon.svg",
                title: copy("settings.preferences.appearance.title", "Apparence", "Appearance"),
                subtitle: copy(
                  "settings.preferences.appearance.subtitle",
                  "Utiliser le thème du système, clair ou sombre",
                  "Use system theme, light or dark"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.change", "Changer", "Change")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/appearance"),
              },
              {
                leading: "/icons/appointments/video-call/newspaper.svg",
                title: copy("settings.preferences.news.title", "Actualités et mises à jour", "News & updates"),
                subtitle: copy(
                  "settings.preferences.news.subtitle",
                  "Préférences e-mail et push",
                  "Email & push preferences"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.manage", "Gérer", "Manage")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/notifications"),
              },
              {
                leading: "/icons/bell-dot.svg",
                title: copy(
                  "settings.preferences.alertsSounds.title",
                  "Alertes et sons",
                  "Alerts & Sounds"
                ),
                subtitle: copy(
                  "settings.preferences.alertsSounds.subtitle",
                  "Son et vibration des messages",
                  "Message sound and vibration"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.manage", "Gérer", "Manage")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/alerts-sounds"),
              },
              {
                leading: "/icons/list-items/notifications_alarm.svg",
                title: copy(
                  "settings.preferences.referralSharing.title",
                  "Partage des orientations",
                  "Referral sharing"
                ),
                subtitle: copy(
                  "settings.preferences.referralSharing.subtitle",
                  "Gérer les autorisations récurrentes entre cliniciens",
                  "Manage recurring clinician-pair permissions"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.manage", "Gérer", "Manage")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/referral-sharing"),
              },
              {
                leading: "/icons/list-items/accessibility_new.svg",
                title: copy(
                  "settings.preferences.accessibility.title",
                  "Accessibilité et Fola",
                  "Accessibility & Fola"
                ),
                subtitle: copy(
                  "settings.preferences.accessibility.subtitle",
                  "Options d’assistance et soutien IA",
                  "Assistive options and AI support"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.manage", "Gérer", "Manage")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/accessibility-fola"),
              },
            ]}
          />
        </section>

        {/* Account section */}
        <section className="space-y-3">
          <h2 className="text-title-small text-on-surface-var">
            {copy("settings.preferences.sections.account", "Compte", "Account")}
          </h2>

          <List
            items={[
              {
                leading: "/icons/list-items/calendar_clock.svg",
                title: copy(
                  "settings.preferences.plans.title",
                  "Plans et abonnements",
                  "Plans & subscriptions"
                ),
                subtitle: copy(
                  "settings.preferences.plans.subtitle",
                  "Votre plan AfriMed™ et ses options",
                  "Your AfriMed™ plan and add-ons"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.manage", "Gérer", "Manage")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/plans"),
              },
              {
                leading: "/icons/list-items/receipt.svg",
                title: copy(
                  "settings.preferences.transactions.title",
                  "Transactions et factures",
                  "Transactions & invoices"
                ),
                subtitle: copy(
                  "settings.preferences.transactions.subtitle",
                  "Voir l’historique et télécharger les reçus",
                  "View history and download receipts"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.open", "Ouvrir", "Open")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/transactions"),
              },
              {
                leading: "/icons/list-items/download.svg",
                title: copy("settings.preferences.downloads.title", "Téléchargements", "Downloads"),
                subtitle: copy(
                  "settings.preferences.downloads.subtitle",
                  "Exporter les données, rapports et résumés",
                  "Export data, reports and summaries"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.open", "Ouvrir", "Open")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/downloads"),
              },
              {
                leading: "/icons/list-items/delete1.svg",
                title: copy(
                  "settings.preferences.closeAccount.title",
                  "Fermer, suspendre ou supprimer le compte",
                  "Close, pause or delete account"
                ),
                subtitle: copy(
                  "settings.preferences.closeAccount.subtitle",
                  "Nous vous guiderons d’abord vers des options plus sûres",
                  "We will guide you through safer options first"
                ),
                trailing: (
                  <span className="text-body-small text-[rgb(var(--afrimed-primary))]">
                    {copy("common.review", "Examiner", "Review")}
                  </span>
                ),
                onClick: () => nav("/settings/patient/preferences/account-lifecycle"),
                divider: false,
              },
            ]}
          />
        </section>
      </div>
    </SettingsScreenShell>
  );
}
